import { OrderStatus } from '@lombok/shared';
import type { OrderStatus as OrderStatusType } from '@lombok/shared';

/**
 * Spanish labels + groupings for the orders slice. Stored values stay snake_case
 * (from @lombok/shared); only what users see is Spanish. The status pill colors
 * live in StatusPill (shared map) — these are the order-specific extras: approval
 * reason codes and the list filter tabs.
 */

/**
 * Approval reason codes emitted by the backend (orderService.evaluateApproval) →
 * a human Spanish explanation. Unknown codes fall back to the raw code so a new
 * backend reason never renders blank.
 */
export const APPROVAL_REASON_LABELS_ES: Record<string, string> = {
  account_pending_approval: 'La cuenta del cliente está en revisión.',
  customer_on_hold: 'El cliente está en espera (on hold).',
  credit_limit_exceeded: 'El pedido supera el crédito disponible del cliente.',
  overdue_balance: 'El cliente tiene saldo vencido.',
  order_value_threshold: 'El monto del pedido supera el umbral que requiere aprobación.',
};

/** The same reasons worded for the customer who placed the order (customer portal). */
export const APPROVAL_REASON_LABELS_OWN_ES: Record<string, string> = {
  account_pending_approval: 'Tu cuenta está en revisión. Tu pedido se atenderá cuando se apruebe.',
  customer_on_hold: 'Tu cuenta está en espera. Tu vendedor te contactará.',
  credit_limit_exceeded: 'El pedido supera tu crédito disponible.',
  overdue_balance: 'Tienes saldo vencido. Ponte al día para que podamos continuar.',
  order_value_threshold: 'El monto del pedido requiere aprobación de nuestro equipo.',
};

/**
 * Resolve an approval reason code to its Spanish label (raw code as fallback). `own` words it for the
 * customer who placed the order instead of for staff.
 */
export function approvalReasonLabel(code: string, own = false): string {
  return (own ? APPROVAL_REASON_LABELS_OWN_ES : APPROVAL_REASON_LABELS_ES)[code] ?? APPROVAL_REASON_LABELS_ES[code] ?? code;
}

/** A list filter tab: a label + the status it filters by (`undefined` = "Todos"). */
export interface OrderStatusTab {
  label: string;
  status?: OrderStatusType;
}

/**
 * Filter tabs for the Pedidos list. Order mirrors the lifecycle so the most
 * actionable states (enviado, pendiente de aprobación) sit up front.
 */
export const ORDER_STATUS_TABS: readonly OrderStatusTab[] = [
  { label: 'Borrador', status: OrderStatus.DRAFT },
  { label: 'Enviado', status: OrderStatus.SUBMITTED },
  { label: 'Pendiente de aprobación', status: OrderStatus.PENDING_APPROVAL },
  { label: 'Aprobado', status: OrderStatus.APPROVED },
  { label: 'En preparación', status: OrderStatus.PICKING },
  { label: 'Despachado', status: OrderStatus.DISPATCHED },
  { label: 'Entregado', status: OrderStatus.DELIVERED },
  { label: 'Rechazado', status: OrderStatus.REJECTED },
  { label: 'Cancelado', status: OrderStatus.CANCELLED },
];

/**
 * Filter tabs for the warehouse fulfillment queue. Only the lifecycle states the
 * warehouse acts on, ordered by the flow: aprobado → preparar → despachar →
 * entregar. The list is server-scoped to the caller, so a concrete status is
 * always sent (no "Todos").
 */
export const WAREHOUSE_STATUS_TABS: readonly OrderStatusTab[] = [
  { label: 'Aprobado', status: OrderStatus.APPROVED },
  { label: 'En preparación', status: OrderStatus.PICKING },
  { label: 'Despachado', status: OrderStatus.DISPATCHED },
  { label: 'Entregado', status: OrderStatus.DELIVERED },
];
